import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../utils/api";

const AuthContext = createContext(null);

// Permission refresh interval (5 minutes)
const PERMISSION_REFRESH_INTERVAL = 5 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissionRefreshInterval, setPermissionRefreshInterval] = useState(null);

  // Fetch current user data from backend
  const fetchCurrentUser = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setUser(null);
        setLoading(false);
        return null;
      }

      const response = await api.get("/api/auth/me");
      if (response.data.success && response.data.user) {
        setUser(response.data.user);
        return response.data.user;
      }
      return null;
    } catch (error) {
      console.error("Error fetching current user:", error);
      // If token is invalid, clear it
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        setUser(null);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Start periodic permission refresh
  const startPermissionRefresh = useCallback(() => {
    // Clear any existing interval
    if (permissionRefreshInterval) {
      clearInterval(permissionRefreshInterval);
    }

    // Set up periodic refresh
    const intervalId = setInterval(() => {
      fetchCurrentUser();
    }, PERMISSION_REFRESH_INTERVAL);

    setPermissionRefreshInterval(intervalId);
  }, [fetchCurrentUser, permissionRefreshInterval]);

  // Stop permission refresh
  const stopPermissionRefresh = useCallback(() => {
    if (permissionRefreshInterval) {
      clearInterval(permissionRefreshInterval);
      setPermissionRefreshInterval(null);
    }
  }, [permissionRefreshInterval]);

  // Login function
  const login = async (username, password) => {
    try {
      const response = await api.post("/api/auth/login", { username, password });
      const { token, user: userData } = response.data;

      if (!token) {
        throw new Error("No token received from server");
      }

      // Store only the token in localStorage (not user data)
      localStorage.setItem("token", token);

      // Set user in memory
      setUser(userData);

      // Start permission refresh
      startPermissionRefresh();

      return response.data;
    } catch (error) {
      console.error("Login error:", error);
      throw new Error(error.response?.data?.message || "Login failed");
    }
  };

  // Logout function
  const logout = useCallback(() => {
    stopPermissionRefresh();
    localStorage.removeItem("token");
    setUser(null);
  }, [stopPermissionRefresh]);

  // Check if user has specific permission
  const hasPermission = useCallback((permission) => {
    if (!user) return false;

    // Admins have all permissions
    if (user.role === "admin") {
      return true;
    }

    // Check if admin_team user has the specific permission
    if (user.role === "admin_team") {
      return user.permissions && user.permissions.includes(permission);
    }

    return false;
  }, [user]);

  // Check if user has any of the specified permissions
  const hasAnyPermission = useCallback((permissions) => {
    if (!user) return false;

    // Admins have all permissions
    if (user.role === "admin") {
      return true;
    }

    // Check if admin_team user has any of the specified permissions
    if (user.role === "admin_team") {
      return permissions.some(
        (permission) => user.permissions && user.permissions.includes(permission)
      );
    }

    return false;
  }, [user]);

  // Check if user has column permission for investors page
  const hasColumnPermission = useCallback((columnKey) => {
    if (!user) return false;

    // Admins have all column permissions
    if (user.role === "admin") {
      return true;
    }

    // For admin_team, check specific column permission
    if (user.role === "admin_team") {
      const columnPermission = `investors.column.${columnKey}`;
      return user.permissions && user.permissions.includes(columnPermission);
    }

    return false;
  }, [user]);

  // Get all column permissions for current user (for investors page)
  const getColumnPermissions = useCallback(() => {
    if (!user) return {};

    // Admins have all column permissions
    if (user.role === "admin") {
      return {
        mobile_number: true,
        email: true,
        wallet_address: true,
        purchase_date: true,
        return_date: true,
        status: true,
        person_place: true,
        referral: true,
        token_number: true,
        scheme_name: true,
        additional_info: true,
        transaction_hash: true,
        transaction_date: true,
        from_wallet: true,
        to_wallet: true,
        usdt_received: true,
        transaction_remarks: true,
        actions: true,
      };
    }

    // For admin_team, build permissions object from user's column permissions
    if (user.role === "admin_team") {
      const columnPermissions = {};
      const columnKeys = [
        'mobile_number', 'email', 'wallet_address', 'purchase_date',
        'return_date', 'status', 'person_place', 'referral', 'token_number',
        'scheme_name', 'additional_info', 'transaction_hash', 'transaction_date',
        'from_wallet', 'to_wallet', 'usdt_received', 'transaction_remarks', 'actions'
      ];

      columnKeys.forEach(key => {
        columnPermissions[key] = user.permissions && 
          user.permissions.includes(`investors.column.${key}`);
      });

      return columnPermissions;
    }

    return {};
  }, [user]);

  // Get user role
  const getUserRole = useCallback(() => {
    return user?.role || null;
  }, [user]);

  // Check if authenticated
  const isAuthenticated = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) return false;

    try {
      // Basic JWT expiration check
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;

      if (payload.exp && payload.exp < currentTime) {
        logout();
        return false;
      }

      return true;
    } catch (error) {
      logout();
      return false;
    }
  }, [logout]);

  // Initialize on mount - check for existing token and fetch user
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        await fetchCurrentUser();
        startPermissionRefresh();
      } else {
        setLoading(false);
      }
    };

    initAuth();

    // Cleanup on unmount
    return () => {
      stopPermissionRefresh();
    };
  }, []);

  const value = {
    user,
    loading,
    login,
    logout,
    hasPermission,
    hasAnyPermission,
    hasColumnPermission,
    getColumnPermissions,
    getUserRole,
    isAuthenticated,
    refreshUser: fetchCurrentUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
